import { Box, Button, Grid2 as Grid, InputAdornment, Tooltip, Typography } from "@mui/material";
import {
  ArrayInput,
  BooleanInput,
  DateInput,
  Form,
  NumberInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useNotify,
  useRefresh,
  useResourceContext,
  useUpdate,
  type RaRecord,
  type Validator,
} from "react-admin";
import { useFormState, useWatch, type FieldValues } from "react-hook-form";
import type { ReactNode } from "react";
import type { EditorField } from "@/components/common/EditableGrid";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { fromISO, toISO } from "@/utils/formatters/dateFormatter";
import { FieldValue } from "./DetailsSection";
import { fieldLabel, getByPath, groupFields, isSet } from "./detailsFields";

interface InPlaceSectionFormProps {
  fields: EditorField[];
  record: RaRecord;
  /** Lower-case section name for the buttons' aria-labels ("Save identity"). */
  name: string;
  /** Called after a successful save and on Cancel. */
  onDone: () => void;
}

const INPUT_SX = {
  mt: 0,
  "& .MuiInput-root": { mt: 0, fontSize: "0.875rem" },
  "& .MuiInputBase-input": { py: 0.25 },
} as const;

/**
 * The claimant PATCH treats null and "" as "leave unchanged" (claimants.go
 * stringToPtr + COALESCE), so a blanked field would show "Saved" and change
 * nothing. Refuse to submit a cleared value instead.
 */
const noClear =
  (before: unknown): Validator =>
  (value: unknown) =>
    isSet(before) && (value === "" || value === null || value === undefined)
      ? "Can't be cleared here. Enter a value."
      : undefined;

const common = (field: EditorField, record: RaRecord) => {
  const own: Validator[] = field.validate ? (Array.isArray(field.validate) ? field.validate : [field.validate]) : [];
  const validate: Validator[] = [...own, noClear(getByPath(record, field.source))];
  return {
    source: field.source,
    label: false as const,
    variant: "standard" as const,
    size: "small" as const,
    margin: "none" as const,
    helperText: false as const,
    validate,
    fullWidth: true,
    sx: INPUT_SX,
  };
};

const dateProps = { parse: toISO, format: fromISO } as const;

const ReadOnlyValue = ({ field, record }: { field: EditorField; record: RaRecord }) => (
  <Tooltip title={field.readOnlyReason ?? ""}>
    <Typography component="span" variant="body2" color="text.secondary">
      <FieldValue field={field} value={getByPath(record, field.source)} />
    </Typography>
  </Tooltip>
);

/** The input for one field, by its editor type; anything this form does not edit reads as a fact. */
const FieldInput = ({ field, record }: { field: EditorField; record: RaRecord }) => {
  if (field.readOnlyReason || field.type === "readonly") return <ReadOnlyValue field={field} record={record} />;
  // The claimant PATCH has no client_id_ref (ClaimantUpdateParams); a change would be dropped silently.
  if (field.type === "reference") return <ReadOnlyValue field={field} record={record} />;
  switch (field.type) {
    case "date":
      return <DateInput {...common(field, record)} {...dateProps} />;
    case "number":
      return <NumberInput {...common(field, record)} />;
    case "dollar":
      return (
        <NumberInput
          {...common(field, record)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      );
    case "select":
      return <SelectInput {...common(field, record)} choices={field.choices ?? []} />;
    case "boolean":
      return (
        <BooleanInput
          {...common(field, record)}
          validate={field.validate}
          sx={{ "& .MuiFormControlLabel-root": { mr: 0 } }}
        />
      );
    case "dateRangeArray":
      return (
        <ArrayInput source={field.source} label={false} helperText={false} sx={{ mt: 0, mb: 0 }}>
          <SimpleFormIterator
            inline
            disableReordering
            disableClear
            sx={{ "& .RaSimpleFormIterator-line": { py: 0.25 } }}
          >
            <DateInput
              source="start_date"
              label="From"
              variant="standard"
              size="small"
              helperText={false}
              {...dateProps}
            />
            <DateInput source="end_date" label="To" variant="standard" size="small" helperText={false} {...dateProps} />
          </SimpleFormIterator>
        </ArrayInput>
      );
    case "email":
      return <TextInput {...common(field, record)} type="email" />;
    case "text":
    case undefined:
      return <TextInput {...common(field, record)} />;
    default:
      return <ReadOnlyValue field={field} record={record} />;
  }
};

/** Label left, input right: the read view's row with the value swapped for its input. */
const FactInput = ({ field, record }: { field: EditorField; record: RaRecord }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Box
      component="label"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 0.5,
        minHeight: 41,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ width: "45%", flexShrink: 0 }}>
        {fieldLabel(field)}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <FieldInput field={field} record={record} />
      </Box>
    </Box>
  </Grid>
);

/**
 * `showWhen` against the live form values, so ticking "Is retired" reveals the
 * retirement date at once (the grid evaluated it the same way; the read view
 * evaluates it against the record).
 */
const WhenShown = ({ field, children }: { field: EditorField; children: ReactNode }) => {
  const negated = !!field.showWhen?.startsWith("!");
  const path = field.showWhen ? (negated ? field.showWhen.slice(1) : field.showWhen) : undefined;
  const value = useWatch({ name: path ?? "__none__", disabled: !path });
  if (!path) return <>{children}</>;
  const shown = negated ? !value : !!value;
  return shown ? <>{children}</> : null;
};

const GroupCaption = ({ children }: { children: ReactNode }) => (
  <Grid size={12}>
    <Typography
      component="h3"
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "text.secondary",
        mt: 1.5,
        mb: 0.25,
      }}
    >
      {children}
    </Typography>
  </Grid>
);

/** Save is live only once something changed; Cancel always. */
const Footer = ({ name, onCancel }: { name: string; onCancel: () => void }) => {
  const { isDirty, isSubmitting } = useFormState();
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
      <Button
        size="small"
        variant="text"
        onClick={onCancel}
        aria-label={`Cancel ${name}`}
        sx={{ textTransform: "none", color: "text.secondary" }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        size="small"
        variant="contained"
        disableElevation
        disabled={!isDirty || isSubmitting}
        aria-label={`Save ${name}`}
        sx={{ textTransform: "none" }}
      >
        Save
      </Button>
    </Box>
  );
};

const setPath = (target: Record<string, unknown>, path: string, value: unknown) => {
  const keys = path.split(".");
  let cur = target;
  keys.slice(0, -1).forEach((k) => {
    if (typeof cur[k] !== "object" || cur[k] === null) cur[k] = {};
    cur = cur[k] as Record<string, unknown>;
  });
  cur[keys[keys.length - 1]] = value;
};

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Flattened policy roots. The data provider nests these back under
 * `policy_data[_selectedPolicyId]` on a claimant update (dataProvider.ts), so a
 * change inside one sends the WHOLE root object (as the grid did) plus the marker,
 * never a partial that could replace the stored object with a fragment.
 */
const POLICY_ROOTS = new Set(["status", "enrollment", "current_policy_data"]);

/**
 * What to PATCH: top-level claimant columns only when changed (the claimant
 * PATCH is sparse); flattened policy roots whole, with the full policy_data map,
 * when anything inside changed; both names when either changes. Verified against
 * coronis-apps by the claimant-authority expert, 2026-10-28.
 */
export const changedFields = (
  fields: EditorField[],
  record: unknown,
  values: Record<string, unknown>
): Record<string, unknown> => {
  const data: Record<string, unknown> = {};
  const roots = new Set<string>();
  fields.forEach((f) => {
    if (f.readOnlyReason || f.type === "readonly") return;
    const before = getByPath(record, f.source);
    const after = getByPath(values, f.source);
    if (same(before, after)) return;
    const root = f.source.split(".")[0];
    if (POLICY_ROOTS.has(root)) roots.add(root);
    else setPath(data, f.source, after === "" ? null : (after ?? null));
  });
  roots.forEach((root) => {
    data[root] = values[root];
  });
  if (roots.size > 0) {
    const rec = record as RaRecord;
    // The API replaces the policy_data JSONB whole (claimants.sql COALESCE($17, policy_data)),
    // so the full map goes too; the provider overlays this policy's roots on it.
    if (rec.policy_data) data.policy_data = rec.policy_data;
    if (typeof rec._selectedPolicyId === "string") data._selectedPolicyId = rec._selectedPolicyId;
  }
  // claimants.go rebuilds claimant_name from the first and last name AS SENT: send both when either changes.
  if ("claimant_first_name" in data || "claimant_last_name" in data) {
    data.claimant_first_name = values.claimant_first_name ?? getByPath(record, "claimant_first_name") ?? null;
    data.claimant_last_name = values.claimant_last_name ?? getByPath(record, "claimant_last_name") ?? null;
  }
  return data;
};

/**
 * In-place editing for a Details section (#7660, Dillon 2026-10-28): the read
 * layout with each value swapped for its input, group captions where the
 * section has groups, Save and Cancel at the foot, one PATCH of what changed.
 * Replaces the Overview grid on the Details tab.
 */
export const InPlaceSectionForm = ({ fields, record, name, onDone }: InPlaceSectionFormProps) => {
  const resource = useResourceContext() ?? "claimants";
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const groups = groupFields(fields, record);
  const showCaptions = groups.length > 1;

  const save = async (values: FieldValues) => {
    const data = changedFields(fields, record, values);
    if (Object.keys(data).length === 0) {
      onDone();
      return;
    }
    await update(
      resource,
      { id: record.id, data, previousData: record },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Saved", { type: "success" });
          refresh();
          onDone();
        },
        onError: (error: unknown) => notifyError(error),
      }
    );
  };

  return (
    <Form record={record} onSubmit={save} data-testid={`${name}-form`}>
      <Grid container columnSpacing={3}>
        {groups.map((group, i) => (
          <Grid container size={12} columnSpacing={3} key={group.title ?? `group-${i}`}>
            {showCaptions && group.title && <GroupCaption>{group.title}</GroupCaption>}
            {group.fields.map((f) => (
              <WhenShown key={f.source} field={f}>
                <FactInput field={f} record={record} />
              </WhenShown>
            ))}
          </Grid>
        ))}
      </Grid>
      <Footer name={name} onCancel={onDone} />
    </Form>
  );
};
